
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LeftPane from '../../components/LeftPane.js';
import Header from '../../components/Header.js';
import { MDBIcon } from 'mdb-react-ui-kit';

import {
    getConversationStats,
    getConversations,
    getConversationMessages,
    sendMessage,
    markAsRead,
    searchConversations
} from '../../services/apiServices.js';
import '../styles/WhatsAppMessagess.css';
import io from 'socket.io-client';
import { getSocketBaseUrl, getSocketIoClientOptions } from '../../utils/socketBaseUrl';

function WhatsAppMessages() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [stats, setStats] = useState({ total: 0, unread: 0, archived: 0, today: 0 });
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [otherUserTyping, setOtherUserTyping] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [sentMessageIds, setSentMessageIds] = useState(new Set());

    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const readReceiptTimeoutRef = useRef(null);
    const [statusCheckInterval, setStatusCheckInterval] = useState(null);


    useEffect(() => {

        const userInfo = localStorage.getItem('loggedUserInfo');
        const gymId = userInfo ? JSON.parse(userInfo).gymid : null;

        if (!gymId) {
            console.warn('No gym ID found for socket connection');
            return;
        }

        const socketUrl = getSocketBaseUrl();
        const socketInstance = io(socketUrl, {
            ...getSocketIoClientOptions(),
            autoConnect: true
        });

        socketInstance.on('connect', () => {
            console.log('Socket connected:', socketInstance.id);
            setIsConnected(true);
            setConnectionStatus('connected');


            socketInstance.emit('join_gym', gymId);

            if (selectedConversation) {
                socketInstance.emit('join_conversation', selectedConversation._id);
            }
        });

        socketInstance.on('disconnect', (reason) => {
            console.log('Socket disconnected:', reason);
            setIsConnected(false);
            setConnectionStatus('disconnected');
        });

        socketInstance.on('connect_error', (error) => {
            console.error('Socket connection error:', error);
            setConnectionStatus('error');
        });

        socketInstance.on('reconnecting', (attemptNumber) => {
            console.log(`Reconnecting attempt ${attemptNumber}`);
            setConnectionStatus('reconnecting');
        });

        socketInstance.on('reconnect', () => {
            console.log('Socket reconnected');
            setConnectionStatus('connected');
            setIsConnected(true);


            if (gymId) {
                socketInstance.emit('join_gym', gymId);
            }
            if (selectedConversation) {
                socketInstance.emit('join_conversation', selectedConversation._id);
            }
        });


        socketInstance.on('new_message', (data) => {
            console.log('New message received via socket:', data);

            if (data.success && data.message) {
                const newMessage = data.message;


                if (sentMessageIds.has(newMessage._id) || sentMessageIds.has(newMessage.messageId)) {
                    console.log('Duplicate message detected, skipping:', newMessage._id);
                    return;
                }


                if (newMessage._id) setSentMessageIds(prev => new Set([...prev, newMessage._id]));
                if (newMessage.messageId) setSentMessageIds(prev => new Set([...prev, newMessage.messageId]));


                setConversations(prev => prev.map(conv => {
                    if (conv._id === newMessage.conversationId) {
                        return {
                            ...conv,
                            lastMessage: {
                                text: newMessage.text,
                                sender: newMessage.sender,
                                timestamp: newMessage.timestamp,
                                status: newMessage.status
                            },
                            updatedAt: new Date()
                        };
                    }
                    return conv;
                }));


                if (selectedConversation && newMessage.conversationId === selectedConversation._id) {
                    setMessages(prev => {

                        const exists = prev.some(msg =>
                            msg._id === newMessage._id ||
                            msg.messageId === newMessage.messageId ||
                            (msg.text === newMessage.text &&
                                msg.sender === newMessage.sender &&
                                Math.abs(new Date(msg.timestamp).getTime() - new Date(newMessage.timestamp).getTime()) < 2000)
                        );

                        if (exists) {
                            console.log('Message already in state, skipping');
                            return prev;
                        }

                        return [...prev, newMessage];
                    });
                }
            }
        });

        // NEW: Handle member messages specifically
        socketInstance.on('member_message_received', (data) => {
            console.log('Member message received:', data);

            if (data.success && data.message && data.conversation) {
                const { message, conversation } = data;
                const { conversationId } = message;
                const isSelectedConversation = selectedConversation && selectedConversation._id === conversationId;


                setConversations(prev => prev.map(conv => {
                    if (conv._id === conversationId) {
                        console.log(`Updating unreadCount for ${conv.memberName}: from ${conv.unreadCount} to ${conversation.unreadCount}`);

                        return {
                            ...conv,
                            lastMessage: conversation.lastMessage,
                            unreadCount: conversation.unreadCount,
                            updatedAt: new Date(conversation.updatedAt)
                        };
                    }
                    return conv;
                }));


                if (isSelectedConversation) {
                    setMessages(prev => {
                        const exists = prev.some(msg =>
                            msg._id === message._id || msg.messageId === message.messageId
                        );
                        if (!exists) {
                            return [...prev, message];
                        }
                        return prev;
                    });


                    markMessageAsRead(message._id, conversationId);


                    setConversations(prev => prev.map(conv => {
                        if (conv._id === conversationId) {
                            return { ...conv, unreadCount: 0 };
                        }
                        return conv;
                    }));
                } else {

                    playNotificationSound();
                }


                setStats(prev => ({
                    ...prev,
                    unread: (prev.unread || 0) + 1,
                    today: (prev.today || 0) + 1
                }));
            }
        });


        socketInstance.on('conversation_updated', (data) => {
            console.log('Conversation updated via socket:', data);


            const isMemberMessage = data.lastMessage && data.lastMessage.sender === 'member';
            const isSelectedConversation = selectedConversation && selectedConversation._id === data.conversationId;

            setConversations(prev => prev.map(conv => {
                if (conv._id === data.conversationId) {
                    let newUnreadCount = conv.unreadCount || 0;


                    if (data.unreadCount !== undefined) {
                        newUnreadCount = data.unreadCount;
                        console.log(`Using backend unreadCount: ${newUnreadCount} for ${conv.memberName}`);
                    }

                    else if (isMemberMessage) {
                        if (!isSelectedConversation) {

                            newUnreadCount = (conv.unreadCount || 0) + 1;
                            console.log(`Incrementing unreadCount for ${conv.memberName}: ${conv.unreadCount} → ${newUnreadCount}`);
                        } else {

                            newUnreadCount = 0;
                        }
                    }

                    return {
                        ...conv,
                        lastMessage: data?.lastMessage ? data?.lastMessage : conv.lastMessage,
                        // updatedAt: new Date(),
                        unreadCount: newUnreadCount
                    };
                }
                return conv;
            }));
        });

        socketInstance.on('message_status_updated', (data) => {
            console.log('Message status updated:', data);

            setMessages(prev => prev.map(msg =>
                msg.messageId === data.messageId || msg._id === data.messageId ? {
                    ...msg,
                    status: data.status,
                    ...(data.status === 'delivered' && { deliveredAt: new Date(data.timestamp) }),
                    ...(data.status === 'read' && { readAt: new Date(data.timestamp) })
                } : msg
            ));
        });


        socketInstance.on('user_typing', (data) => {
            console.log('yping indicator received:', data);
            if (selectedConversation && data.conversationId === selectedConversation._id && data.userId !== 'business') {
                setOtherUserTyping(data.isTyping);


                if (data.isTyping) {
                    setTimeout(() => {
                        setOtherUserTyping(false);
                    }, 3000);
                }
            }
        });

        socketInstance.on('messages_read', (data) => {
            console.log('Messages read event:', data);
            if (selectedConversation && data.conversationId === selectedConversation._id) {

                setMessages(prev => prev.map(msg =>
                    msg.sender === 'business' && msg.status === 'delivered' ? { ...msg, status: 'read' } : msg
                ));
            }
        });

        setSocket(socketInstance);

        return () => {
            if (socketInstance) {
                socketInstance.disconnect();
            }
            if (readReceiptTimeoutRef.current) {
                clearTimeout(readReceiptTimeoutRef.current);
            }
        };
    }, []);


    useEffect(() => {
        if (selectedConversation && messages.length > 0) {
            const memberMessages = messages.filter(msg =>
                msg.sender === 'member' && msg.status !== 'read'
            );

            if (memberMessages.length > 0) {
                memberMessages.forEach(message => {
                    markMessageAsRead(message._id, selectedConversation._id);
                });

                setMessages(prev => prev.map(msg =>
                    msg.sender === 'member' && msg.status !== 'read' ? { ...msg, status: 'read' } : msg
                ));

                setConversations(prev => prev.map(conv => {
                    if (conv._id === selectedConversation._id) {
                        return { ...conv, unreadCount: 0 };
                    }
                    return conv;
                }));

                loadStats();
            }
        }
    }, [selectedConversation, messages]);

    useEffect(() => {
        console.log('=== UNREAD COUNT DEBUG ===');
        conversations.forEach(conv => {
            console.log(`Conversation ${conv.memberName || conv._id}:`, {
                unreadCount: conv.unreadCount,
                type: typeof conv.unreadCount,
                lastMessage: conv.lastMessage?.text?.substring(0, 30),
                lastSender: conv.lastMessage?.sender,
                updatedAt: conv.updatedAt
            });
        });
        console.log('Total conversations:', conversations.length);
    }, [conversations]);

    useEffect(() => {
        loadConversations();
        loadStats();
    }, []);


    useEffect(() => {
        if (socket && selectedConversation) {
            socket.emit('join_conversation', selectedConversation._id);

            loadMessages(selectedConversation._id);
            markConversationAsRead(selectedConversation._id);

            return () => {
                socket.emit('leave_conversation', selectedConversation._id);
                setOtherUserTyping(false);
            };
        }
    }, [socket, selectedConversation]);

    useEffect(() => {
        if (selectedConversation) {
            if (statusCheckInterval) {
                clearInterval(statusCheckInterval);
            }

            const interval = setInterval(async () => {
                await loadMessages(selectedConversation._id);
            }, 10000);

            setStatusCheckInterval(interval);

            return () => {
                clearInterval(interval);
            };
        }
    }, [selectedConversation]);

    useEffect(() => {
        return () => {
            if (statusCheckInterval) {
                clearInterval(statusCheckInterval);
            }
        };
    }, []);


    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const loadConversations = async () => {
        try {
            setLoading(true);
            const gymid = localStorage.getItem("loggedUserInfo")
                ? JSON.parse(localStorage.getItem("loggedUserInfo")).gymid
                : -1;

            console.log('Loading conversations for gym:', gymid);

            const response = await getConversations(gymid);

            if (!response || response === "false") {
                console.error('Failed to load conversations');
                setConversations([]);
                return;
            }

            const responseData = await response.json();
            console.log('Parsed response data:', responseData);

            if (responseData && responseData.success) {
                const conversationsWithUnread = (responseData.data || []).map(conv => {
                    const unreadCount = Number(conv.unreadCount) || 0;
                    console.log(`Conversation ${conv.memberName || conv._id}: unreadCount = ${unreadCount} (was: ${conv.unreadCount})`);

                    return {
                        ...conv,
                        unreadCount: unreadCount,
                        lastMessage: conv.lastMessage || {
                            text: 'No messages yet',
                            sender: 'system',
                            timestamp: conv.updatedAt || conv.createdAt,
                            status: 'sent'
                        }
                    };
                });

                console.log('Conversations with unread counts:', conversationsWithUnread);
                setConversations(conversationsWithUnread);
            } else {
                console.error('API error:', responseData);
                setConversations([]);
            }
        } catch (error) {
            console.error('Error loading conversations:', error);
            setConversations([]);
        } finally {
            setLoading(false);
        }
    };

    const loadMessages = async (conversationId) => {
        try {
            console.log('Loading messages for conversation:', conversationId);
            const response = await getConversationMessages(conversationId);

            if (!response || response === "false") {
                console.error('Failed to load messages');
                return;
            }

            const responseData = await response.json();
            console.log('Messages response:', responseData);

            if (responseData && responseData.success) {
                const messages = responseData.data.messages || [];
                console.log('Loaded messages:', messages.length);
                const newIds = new Set();
                messages.forEach(msg => {
                    if (msg._id) newIds.add(msg._id);
                    if (msg.messageId) newIds.add(msg.messageId);
                });
                setSentMessageIds(prev => new Set([...prev, ...newIds]));

                setMessages(messages);

                if (selectedConversation && selectedConversation._id === conversationId) {
                    const memberMessages = messages.filter(msg =>
                        msg.sender === 'member' && msg.status !== 'read'
                    );

                    if (memberMessages.length > 0) {
                        const updatedMessages = messages.map(msg =>
                            msg.sender === 'member' && msg.status !== 'read' ? { ...msg, status: 'read' } : msg
                        );
                        setMessages(updatedMessages);

                        memberMessages.forEach(msg => {
                            markMessageAsRead(msg._id, conversationId);
                        });

                        setConversations(prev => prev.map(conv => {
                            if (conv._id === conversationId) {
                                return { ...conv, unreadCount: 0 };
                            }
                            return conv;
                        }));
                    }
                }
            } else {
                console.error('Error loading messages:', responseData);
                setMessages([]);
            }
        } catch (error) {
            console.error('Error loading messages:', error);
            setMessages([]);
        }
    };

    const loadStats = async () => {
        try {
            const gymid = localStorage.getItem("loggedUserInfo")
                ? JSON.parse(localStorage.getItem("loggedUserInfo")).gymid
                : -1;

            const response = await getConversationStats(gymid);

            if (!response || response === "false") {
                console.error('Failed to load stats');
                return;
            }

            const responseData = await response.json();
            console.log('Stats response:', responseData);

            if (responseData && responseData.success) {
                setStats(responseData.data || { total: 0, unread: 0, archived: 0, today: 0 });
            }
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    };

    const markConversationAsRead = async (conversationId) => {
        try {
            await markAsRead(conversationId);

            setConversations(prev => prev.map(conv => {
                if (conv._id === conversationId) {
                    return { ...conv, unreadCount: 0 };
                }
                return conv;
            }));

            if (socket) {
                socket.emit('mark_as_read', { conversationId });
            }

            const memberMessages = messages.filter(msg =>
                msg.conversationId === conversationId &&
                msg.sender === 'member' &&
                msg.status !== 'read'
            );

            if (memberMessages.length > 0) {
                memberMessages.forEach(msg => {
                    markMessageAsRead(msg._id, conversationId);
                });
            }

            loadStats();

        } catch (error) {
            console.error('Error marking as read:', error);
        }
    };


    const markMessageAsRead = async (messageId, conversationId) => {
        try {
            if (socket && isConnected) {
                socket.emit('mark_message_read', {
                    messageId,
                    conversationId
                });
            }


            try {
                // await markMessageAsReadAPI(messageId);
            } catch (apiError) {
                console.error('API error marking message as read:', apiError);
            }
        } catch (error) {
            console.error('Error marking message as read:', error);
        }
    };

    const handleConversationSelect = (conv) => {

        setConversations(prev => prev.map(c => {
            if (c._id === conv._id) {
                console.log(`Selecting conversation ${conv._id}, resetting unreadCount from ${c.unreadCount} to 0`);
                return { ...c, unreadCount: 0 };
            }
            return c;
        }));

        setSelectedConversation({ ...conv, unreadCount: 0 });

        setOtherUserTyping(false);
        loadMessages(conv._id);

        markConversationAsRead(conv._id);

    };


    const handleTyping = useCallback(() => {
        if (!socket || !selectedConversation || !isConnected) return;

        setIsTyping(true);

        socket.emit('typing', {
            conversationId: selectedConversation._id,
            isTyping: true,
            userId: 'business'
        });

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        typingTimeoutRef.current = setTimeout(() => {
            setIsTyping(false);
            socket.emit('typing', {
                conversationId: selectedConversation._id,
                isTyping: false,
                userId: 'business'
            });
        }, 1000);
    }, [socket, selectedConversation, isConnected]);

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !selectedConversation || sending) return;

        try {
            setSending(true);


            const tempMessageId = `temp_${Date.now()}`;


            setSentMessageIds(prev => new Set([...prev, tempMessageId]));


            const tempMessage = {
                _id: tempMessageId,
                text: newMessage.trim(),
                sender: 'business',
                status: 'sending',
                timestamp: new Date(),
                conversationId: selectedConversation._id,
                createdAt: new Date().toISOString(),
                isOptimistic: true
            };


            setMessages(prev => [...prev, tempMessage]);


            const messageToSend = newMessage.trim();
            setNewMessage('');


            if (socket && isConnected) {
                console.log('📤 Sending via socket...');

                const messageData = {
                    conversationId: selectedConversation._id,
                    text: messageToSend,
                    sender: 'business',
                    gymId: localStorage.getItem("loggedUserInfo")
                        ? JSON.parse(localStorage.getItem("loggedUserInfo")).gymid
                        : -1,
                    tempMessageId: tempMessageId
                };


                socket.emit('send_message', messageData);

            } else {
                console.log('Sending via API (socket not connected)...');

                try {
                    const response = await sendMessage(selectedConversation._id, messageToSend);

                    if (!response || response === "false") {

                        setMessages(prev => prev.map(msg =>
                            msg._id === tempMessageId ? { ...msg, status: 'failed' } : msg
                        ));
                        return;
                    }

                    const responseData = await response.json();

                    if (responseData && responseData.success) {
                        const finalMessage = responseData.data;


                        if (finalMessage._id) {
                            setSentMessageIds(prev => new Set([...prev, finalMessage._id]));
                        }
                        if (finalMessage.messageId) {
                            setSentMessageIds(prev => new Set([...prev, finalMessage.messageId]));
                        }


                        setMessages(prev => {
                            const filtered = prev.filter(msg => msg._id !== tempMessageId);

                            const exists = filtered.some(msg =>
                                msg._id === finalMessage._id ||
                                msg.messageId === finalMessage.messageId
                            );
                            if (!exists) {
                                return [...filtered, finalMessage];
                            }
                            return filtered;
                        });


                        setConversations(prev => prev.map(conv => {
                            if (conv._id === selectedConversation._id) {
                                return {
                                    ...conv,
                                    lastMessage: {
                                        text: messageToSend,
                                        sender: 'business',
                                        timestamp: new Date(),
                                        status: 'sent'
                                    },
                                    updatedAt: new Date()
                                };
                            }
                            return conv;
                        }));
                    } else {

                        setMessages(prev => prev.map(msg =>
                            msg._id === tempMessageId ? { ...msg, status: 'failed' } : msg
                        ));
                    }
                } catch (error) {
                    console.error('API send error:', error);
                    setMessages(prev => prev.map(msg =>
                        msg._id === tempMessageId ? { ...msg, status: 'failed' } : msg
                    ));
                }
            }

        } catch (error) {
            console.error('Error sending message:', error);

            setMessages(prev => prev.map(msg =>
                msg.isOptimistic && msg.status === 'sending' ? { ...msg, status: 'failed' } : msg
            ));
        } finally {
            setSending(false);
            setIsTyping(false);
        }
    };

    const handleSearch = async () => {
        if (!searchTerm.trim()) {
            loadConversations();
            return;
        }

        try {
            const gymid = localStorage.getItem("loggedUserInfo")
                ? JSON.parse(localStorage.getItem("loggedUserInfo")).gymid
                : -1;

            const response = await searchConversations(searchTerm, gymid);

            if (!response || response === "false") {
                console.error('Search failed');
                return;
            }

            const responseData = await response.json();
            console.log('Search results:', responseData);

            if (responseData && responseData.success) {
                setConversations(responseData.data || []);
            }
        } catch (error) {
            console.error('Error searching:', error);
        }
    };

    const playNotificationSound = () => {
        try {

            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = 800;
            oscillator.type = 'sine';

            gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.5);
        } catch (error) {
            console.log('Could not play notification sound:', error);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const formatTime = (dateString) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (error) {
            return '--:--';
        }
    };

    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            const today = new Date();

            if (date.toDateString() === today.toDateString()) {
                return 'Today';
            }

            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            if (date.toDateString() === yesterday.toDateString()) {
                return 'Yesterday';
            }

            return date.toLocaleDateString();
        } catch (error) {
            return 'Unknown date';
        }
    };

    const getMessageStatusIcon = (status) => {
        switch (status) {
            case 'sending':
                return (
                    <MDBIcon
                        icon="clock"
                        className="msg-status-icon sending"
                    />
                );

            case 'sent':
                return (
                    <MDBIcon
                        icon="check"
                        className="msg-status-icon sent"
                    />
                );

            case 'delivered':
                return (
                    <span className="msg-double-tick delivered">
                        <MDBIcon fas icon="check-double" />
                    </span>
                );

            case 'read':
                return (
                    <span className="msg-double-tick read">
                        <MDBIcon fas icon="check-double" />
                    </span>
                );

            case 'failed':
                return (
                    <MDBIcon
                        icon="exclamation-circle"
                        className="msg-status-icon failed"
                    />
                );

            default:
                return <MDBIcon icon="check" className="msg-status-icon sent" />;
        }
    };


    const groupedMessages = messages.reduce((groups, message) => {
        try {
            const date = formatDate(message.timestamp || message.createdAt);
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(message);
            return groups;
        } catch (error) {
            console.error('Error grouping message:', error, message);
            return groups;
        }
    }, {});

    const handleInputChange = (e) => {
        setNewMessage(e.target.value);
        if (e.target.value.trim().length > 0) {
            handleTyping();
        }
    };

    const handleInputKeyUp = (e) => {
        if (e.target.value.trim().length === 0 && isTyping) {
            setIsTyping(false);
            if (socket && selectedConversation) {
                socket.emit('typing', {
                    conversationId: selectedConversation._id,
                    isTyping: false,
                    userId: 'business'
                });
            }
        }
    };

    const SocketStatus = () => (
        <div className={`socket-status ${connectionStatus}`}>
            <span className="status-dot"></span>
            {connectionStatus === 'connected' ? 'Live' :
                connectionStatus === 'reconnecting' ? 'Reconnecting...' :
                    connectionStatus === 'error' ? 'Connection Error' : 'Offline'}
        </div>
    );
    const getUnreadCount = () => {
        if (!selectedConversation) return 0;
        const conversation = conversations.find(conv => conv._id === selectedConversation._id);
        return conversation?.unreadCount || 0;
    };

    const handleRefreshConversations = () => {
        loadConversations();
        loadStats();
    };

    return (
        <div className="gym-container">
            <LeftPane isactive="16" />
            <Header />
            <div className="gym-main gym-main-div rightContent">
                <div className="whatsapp-messages-container">

                    {/* Header */}
                    <div className="whatsapp-messages-header">
                        <div className="header-left">
                            <h3>WhatsApp Messages</h3>
                            <div className="stats-bar">

                                <div className="stat-item">
                                    <MDBIcon icon="comments" />
                                    <span>{stats?.total || 0} Total</span>
                                </div>
                                <div className="stat-item">
                                    <MDBIcon icon="envelope" />
                                    <span>{stats?.unread || 0} Unread</span>
                                </div>
                                <div className="stat-item">
                                    <MDBIcon icon="calendar-day" />
                                    <span>{stats?.today || 0} Today</span>
                                </div>
                            </div>
                        </div>
                        <div className="header-right d-flex align-items-center">
                            <div
                                className="search-box d-flex align-items-center bg-light border rounded-pill px-3"
                                style={{ height: '40px', width: '260px' }}
                            >
                                <MDBIcon icon="search" className="text-muted me-2" />
                                <input
                                    type="text"
                                    className="form-control border-0 bg-transparent p-0"
                                    placeholder="Search conversations..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                    style={{ boxShadow: 'none' }}
                                />
                            </div>

                            <button
                                onClick={handleSearch}
                                className="btn btn-primary btn-sm ms-2 rounded-pill px-3"
                            >
                                Search
                            </button>

                            <button
                                onClick={handleRefreshConversations}
                                className="btn btn-outline-secondary btn-sm ms-2"
                                title="Refresh conversations and stats"
                            >
                                <MDBIcon icon="sync-alt" /> Refresh All
                            </button>
                        </div>
                    </div>

                    <div className="whatsapp-messages-content">

                        {/* Left sidebar - Conversations list */}
                        <div className="conversations-sidebar">
                            <div className="sidebar-header">
                                <h4>Conversations</h4>
                                <MDBIcon
                                    icon="sync-alt"
                                    className={`refresh-icon ${loading ? 'spinning' : ''}`}
                                    onClick={handleRefreshConversations}
                                    title="Refresh conversations"
                                />
                            </div>

                            {loading ? (
                                <div className="loading-conversations">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : conversations && Array.isArray(conversations) && conversations.length > 0 ? (
                                <div className="conversations-list">
                                    {conversations.map(conv => (
                                        <div
                                            key={conv._id}
                                            className={`conversation-item ${selectedConversation?._id === conv._id ? 'active' : ''}`}
                                            onClick={() => handleConversationSelect(conv)}
                                        >
                                            <div className="conversation-avatar">
                                                <MDBIcon icon="user-circle" />
                                                {/* {conv.unreadCount > 0 && (
                                                    <span className="unread-badge">{conv.unreadCount}</span>
                                                )} */}
                                            </div>
                                            <div className="conversation-info">
                                                <div className="conversation-header">
                                                    <h5>{conv.memberName || 'Unknown'}</h5>
                                                    <span className="conversation-time">
                                                        {formatTime(conv.lastMessage.timestamp)}
                                                    </span>
                                                </div>
                                                <div className="conversation-preview">
                                                    <p className="last-message">
                                                        {conv.lastMessage?.text || 'No messages yet'}
                                                        {conv.lastMessage?.sender === 'business' && (
                                                            <span className="message-status-small">
                                                                {conv.lastMessage?.status === 'sent' && ' ✓'}
                                                                {conv.lastMessage?.status === 'delivered' && ' ✓✓'}
                                                                {conv.lastMessage?.status === 'read' && ' ✓✓'}
                                                            </span>
                                                        )}
                                                    </p>
                                                    {conv.unreadCount > 0 && (
                                                        <span className="unread-dot">{conv.unreadCount}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="no-conversations">
                                    <MDBIcon icon="comments" />
                                    <p>No conversations yet</p>
                                    {!loading && <small className="text-muted">Click refresh to load conversations</small>}
                                </div>
                            )}
                        </div>


                        {/* Right side - Chat area */}
                        <div className="chat-area">
                            {selectedConversation ? (
                                <>
                                    {/* Chat header */}
                                    <div className="chat-header">
                                        <div className="chat-contact-info">
                                            <div className="contact-avatar">
                                                <MDBIcon icon="user-circle" />
                                                {getUnreadCount() > 0 && (
                                                    <span className="unread-badge">{getUnreadCount()}</span>
                                                )}
                                            </div>
                                            <div className="contact-details">
                                                <h4>{selectedConversation.memberName || 'Unknown'}</h4>
                                                <p className="contact-phone">{selectedConversation.phone || 'No phone'}</p>
                                                {otherUserTyping && (
                                                    <div className="typing-indicator">
                                                        <span className="typing-dots">
                                                            <span>.</span><span>.</span><span>.</span>
                                                        </span>
                                                        <span>typing</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="chat-actions">
                                            <button className="action-btn">
                                                <MDBIcon icon="phone-alt" />
                                            </button>
                                            <button className="action-btn">
                                                <MDBIcon icon="video" />
                                            </button>
                                            <button className="action-btn">
                                                <MDBIcon icon="ellipsis-v" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Connection warning */}
                                    {!isConnected && (
                                        <div className="connection-warning">
                                            <MDBIcon icon="exclamation-triangle" className="me-2" />
                                            You're offline. Messages will be sent when reconnected.
                                        </div>
                                    )}

                                    {/* Messages container */}
                                    <div className="messages-container" ref={chatContainerRef}>
                                        {Object.entries(groupedMessages).map(([date, dateMessages]) => (
                                            <div key={date} className="message-date-group">
                                                <div className="date-separator">
                                                    <span>{date}</span>
                                                </div>
                                                {dateMessages.map(message => (
                                                    <div
                                                        key={message._id || message.messageId}
                                                        className={`message-wrapper ${message.sender === 'business' ? 'sent' : 'received'}`}
                                                    >
                                                        <div className="message-bubble">
                                                            <div className="message-text">{message.text || '[No text]'}</div>
                                                            <div className="message-footer">
                                                                <span className="message-time">
                                                                    {formatTime(message.timestamp || message.createdAt)}
                                                                </span>
                                                                {message.sender === 'business' && (
                                                                    <span className="message-status">
                                                                        {getMessageStatusIcon(message.status || 'sent')}
                                                                    </span>
                                                                )}
                                                                {message.sender === 'member' && message.status !== 'read' && (
                                                                    <span className="unread-indicator" title="Unread">
                                                                        •
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {message.status === 'failed' && (
                                                                <div className="message-error">
                                                                    <small>Failed to send. Tap to retry.</small>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                        <div ref={messagesEndRef} />
                                    </div>

                                    {/* Message input */}
                                    <div className="message-input-container">
                                        <div className="input-actions">
                                            <button className="input-action-btn">
                                                <MDBIcon icon="paperclip" />
                                            </button>
                                            <button className="input-action-btn">
                                                <MDBIcon icon="image" />
                                            </button>
                                        </div>
                                        <div className="message-input-wrapper">
                                            <input
                                                type="text"
                                                className="message-input"
                                                placeholder={isConnected ? "Type a message..." : "Connecting..."}
                                                value={newMessage}
                                                onChange={handleInputChange}
                                                onKeyUp={handleInputKeyUp}
                                                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                                disabled={sending || !isConnected}
                                                autoFocus
                                            />
                                            {isTyping && (
                                                <div className="own-typing-indicator">
                                                    <small>You're typing...</small>
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            className="send-button"
                                            onClick={handleSendMessage}
                                            disabled={!newMessage.trim() || sending || !isConnected}
                                            title={!isConnected ? "Reconnecting..." : "Send message"}
                                        >
                                            {sending ? (
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Sending...</span>
                                                </div>
                                            ) : (
                                                <MDBIcon icon="paper-plane" />
                                            )}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="no-chat-selected">
                                    <div className="empty-state">
                                        <MDBIcon icon="comments" className="empty-icon" />
                                        <h4>Select a conversation</h4>
                                        <p>Choose a conversation from the list to start messaging</p>
                                        {/* <SocketStatus /> */}
                                        {/* <button
                                            className="btn btn-outline-primary mt-3"
                                            onClick={() => socket?.connect()}
                                            disabled={isConnected}
                                        >
                                            {isConnected ? 'Connected' : 'Reconnect Socket'}
                                        </button> */}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default WhatsAppMessages;